// engineListener.ts
import { Bid } from '../../app/types';
import { TableState } from '../../slices/tableSlice';
import { bridgeWarGui } from '../../env';
import { closeBWarWindow } from '../../utils/mixed';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
    engineApproveClaim,
    engineApproveUndo,
    engineButtonClicked,
    engineCancelBidExplanation,
    engineExplainBid,
    engineExplainMyBid, engineHTMLLink, engineLoadBoardReviewData,
    engineMakeBid,
    engineMakePlay, engineReloadBoardReviewData,
} from './engineCommands';
import { getCurrentGameEngine } from '../gameEngineRegistry';
import { modalCencede, modalMakeClaim } from '../../utils/modal.helper';

export const engineListener = createListenerMiddleware();

engineListener.startListening({
    actionCreator: engineButtonClicked,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}

        const { id } = action.payload;

        switch (id) {
            case 'next':
                engine.clientRequestsNextPoint();
                break;
            case 'nexthand':
                engine.clientRequestsNextHand();
                break;
            case 'prevhand':
                engine.clientRequestsPrevHand();
                break;
            case 'back':
                engine.clientRequestsPreviousPoint();
                break;
            case 'playDeal':
                engine.clientRequestsNextPoint();
                break;
            case 'practice':
                engine.clientRequestsTraining();
                break;
            case 'autoBid':
                engine.clientRequestsAutoBid();
                break;
            case 'answer':
                engine.clientRequestsAnswer();
                break;
            case 'undo':
                engine.clientRequestsUndo();
                break;
            case 'replay':
                engine.clientRequestsReplay();
                break;
            case 'skip':
                engine.clientRequestsSkip();
                break;
            case 'favorite':
                engine.clientRequestsFav('Some notes');
                break;
            case 'claim': {
                const modal = modalMakeClaim(
                    engine.props.table.ewtricks + engine.props.table.nstricks,
                    engine.props.output_setMakeClaim,
                );
                engine.props.app_addModal(modal);
                // this.clientRequestsClaim(0);
                break;
            }
            case 'concede': {
                const win = engine.props.table.nstricks >= engine.props.table.bridgeWarGoal;
                const modal = modalCencede(
                    engine.props.table.ewtricks + engine.props.table.nstricks,
                    win,
                    engine.props.output_setMakeClaim,
                );
                engine.props.app_addModal(modal);
                // this.clientRequestsClaim(0);
                break;
            }
            case 'acceptClaim':
                engine.clientRequestsAcceptClaim();
                break;
            case 'auxiliaryCancel':
                try {
                    engine.props.table_setAuxiliaryMessage({
                        position: Array.isArray(action.payload.value)
                                  ? (action.payload.value[1] as keyof TableState['auxiliaryMessages'])
                                  : (action.payload.value as keyof TableState['auxiliaryMessages']),
                        message: undefined,
                    });
                    engine.props.table_setAuxiliaryMessage({ position: 'center', message: undefined });
                } catch (e) {
                    //
                }
                // this.clientRequestsAcceptClaim();
                break;
            case 'auxiliaryOk':
                if (Array.isArray(action.payload.value)) {
                    engine.props.table_setAuxiliaryMessage({
                        position: action.payload.value[1] as keyof TableState['auxiliaryMessages'],
                        message: undefined,
                    });
                    engine.clientRequestsMakeCall(action.payload.value[0] as Bid);
                } else {
                    engine.clientRequestsNextPoint();
                }
                break;
            case 'auxiliaryAuxiliary':
                engine.processAuxiliaryButton(action.payload);
                break;
            case 'dashboard':
                if (bridgeWarGui) {
                    closeBWarWindow(engine.returnURL);
                } else {
                    parent.postMessage('reloaddash', '*');
                    parent.location.href = engine.returnURL;
                }
                break;
            case 'closeTrick': {
                engine.clientRequestsCloseTrick();
                break;
            }
            case 'index': {
                engine.toggleIndexMenu();
                break;
            }
            case 'indexButton': {
                engine.clientRequestsTopicAtIndex(action.payload.value as number);
                break;
            }
            case 'showAll': {
                engine.clientRequestToggleVisibility();
                break;
            }
            case 'showSharkSettings': {
                engine.props.app_setShowSettings(true);
                break;
            }
            case 'showInfo': {
                if (engine.props.table.walkThrough) {
                    engine.props.table_setWalkTrough(undefined);
                }else {
                    engine.updateBoardData(undefined);
                    engine.props.table_setWalkTrough({ content: `Current Board` });
                }
                break;
            }
            case 'showLeaderBoard': {
                engine.showLeaderBoard();
                break;
            }
            default:
                console.warn('pressed button is unhandled');
        }
    },
});

engineListener.startListening({
    actionCreator: engineMakeBid,
    effect: (action) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientRequestsMakeCall(action.payload);
    },
});

engineListener.startListening({
    actionCreator: engineMakePlay,
    effect: (action) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientRequestsMakePlay(action.payload);
    },
});

// 1) Explain some bid in the auction
engineListener.startListening({
    actionCreator: engineExplainBid,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientRequestsExplCall(action.payload);
    },
});

// 2) Explain *my* bid
engineListener.startListening({
    actionCreator: engineExplainMyBid,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientRequestOwnBidExplanation(action.payload);
    },
});

// 3) Cancel explanation
engineListener.startListening({
    actionCreator: engineCancelBidExplanation,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.cancelBidExplanation();
    },
});

engineListener.startListening({
    actionCreator: engineApproveClaim,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientResponseClaim(action.payload);
    },
});

engineListener.startListening({
    actionCreator: engineApproveUndo,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        //engine.clientResponseClaim(action.payload);
    },
});

engineListener.startListening({
    actionCreator: engineHTMLLink,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.clientRequestsHTMLLink(action.payload);
    },
});

engineListener.startListening({
    actionCreator: engineLoadBoardReviewData,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.loadBoardReview(action.payload);
    },
});

engineListener.startListening({
    actionCreator: engineReloadBoardReviewData,
    effect: (action, api) => {
        const engine = getCurrentGameEngine();
        if (!engine) {return;}
        engine.loadBoardReview(action.payload);
    },
});

